import React, { Key, useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  Collapse,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Table,
  Tooltip,
} from "antd";
import Tree, { DataNode } from "antd/lib/tree";
import { Link } from "react-router-dom";
import { uniqueId } from "lodash";
import { FormInstance } from "antd/es/form/Form";
import { PlusCircleFilled } from "@ant-design/icons";
import moment from "moment";
import { TableColumn } from "../../../../lib/interfaces";
import {
  checkIsLoading,
  displayFormat,
  getDictionaryItemById,
  toDisplayFormat,
  toHttpFormatEndOfDay,
  toHttpFormatStartOfDay,
  useActiveModal,
} from "../../../../lib/utils";
import {
  IOfferingGroup,
  IOfferingGroupCreate,
  IOfferingGroupForm,
} from "../../types/IOfferingGroup";
import { useOfferingGroupTreeTableData } from "./utils/useOfferingGroupTreeTableData";
import {
  DataNodeOfferingGroup,
  DataNodeOfferingGroupTree,
  IOfferingGroupParamsTableRecord,
} from "./utils/types";
import { ReactComponent as OfferingIcon } from "../../../../img/offering-icon.svg";
import { ReactComponent as GroupOfferingIcon } from "../../../../img/group-offering-icon.svg";
import { ReactComponent as LoupeIcon } from "../../../../img/loupe-icon.svg";
import { IParameterSearchType } from "../../context/PoliciesContext/types";
import { OfferListModal } from "../../../../components/OfferList/OfferListModal/OfferListModal";
import { OfferingGroupsSearchModal } from "../OfferingGroupsSearchModal/OfferingGroupsSearchModal";
import { useOfferList } from "../../../../components/OfferList/utils/useOfferList";
import { Offer } from "../../../../lib/types";
import { useAppDispatch, useAppSelector } from "../../../..";
import {
  addedOfferingGroupMember,
  deleteOfferingGroupMember,
  fetchOfferingGroupIdForTableTree,
  updateOfferingGroupMemberCloseDate,
} from "../../store/slice/offeringGroupsSlice";
import { ReactComponent as SubmitIcon } from "../../../../img/buttons_icons/submitButton_icon.svg";
import { ReactComponent as CancelIcon } from "../../../../img/buttons_icons/cancelButton_icon.svg";
import { ReactComponent as DeleteIcon } from "../../../../img/delete.svg";
import { ReactComponent as CloseIcon } from "../../../../img/versions_icons/closeVersion_icon.svg";
import { hasAccess } from "../../../../components/RolesModule/hasAccess";
import { MENU_ADMIN_OFFERING_GROUP_WRITE } from "../../../../lib/const/permissions";
import { IOfferListSearch } from "../../../../store/slices/offering/types";
import "./OfferingGroupTreeTable.less";

const columnsWidths: Partial<Record<keyof IOfferingGroupForm, string>> = {
  name: "70%",
  fromDate: "270px",
};

interface IProps {
  offeringGroup?: IOfferingGroup;
  offeringGroupParamsForm?: FormInstance<IOfferingGroupForm>;
  mode?: "create" | "view" | "edit_close_date" | "edit";
  mainTreeData?: IOfferingGroupCreate;
  setMainTreeData: React.Dispatch<React.SetStateAction<IOfferingGroupCreate | undefined>>;
}

const OfferingGroupTreeTable = ({
  offeringGroup,
  offeringGroupParamsForm,
  mode,
  mainTreeData,
  setMainTreeData,
}: IProps) => {
  const dispatch = useAppDispatch();

  const {
    offeringGroupsEntityObjectTypeDic,
    offeringGroupsTypesDic,
    addedOfferingGroupMemberLoading,
    updateOfferingGroupMemberCloseDateLoading,
  } = useAppSelector((state) => state.offeringGroups);

  const [modeAddedRow, setModeAddedRow] = useState(mode === "create");

  const [addedOfferingGroup, setAddedOfferingGroup] = useState<IOfferingGroup>();
  const [addedOffering, setAddedOffering] = useState<Offer>();

  const [
    editedCloseDateRecord,
    setEditedCloseDateRecord,
  ] = useState<IOfferingGroupParamsTableRecord>();

  const onClickPlus = () => {
    if (!modeAddedRow) {
      setMainTreeData((prev) => ({
        ...prev,
        offeringGroupMembers: [...(prev?.offeringGroupMembers || []), {}],
      }));
      // В режиме просмотра
      if (offeringGroup) {
        offeringGroupParamsForm &&
          offeringGroupParamsForm.setFieldsValue({
            fromDate: moment(offeringGroup?.fromDate),
            toDate: moment(offeringGroup?.toDate),
            fromDateRecord: moment(offeringGroup?.fromDate),
            toDateRecord: moment(offeringGroup?.toDate),
          });
      } else {
        // При добавлении элемента в группу необходимо копировать Конец периода из атрибутов Группы в поле Конец периода для элемента группы
        const toDate = offeringGroupParamsForm?.getFieldValue("toDate");
        offeringGroupParamsForm &&
          offeringGroupParamsForm.setFieldsValue({
            toDateRecord: toDate ? moment(toDate) : undefined,
          });
      }
      setModeAddedRow(true);
    }
  };

  useEffect(() => {
    if (offeringGroup && mode !== "create") {
      setMainTreeData(offeringGroup);
    }

    if (mode === "create" && offeringGroupParamsForm) {
      // При создании в пустой первый элемент подставляем конечную из атрибутов группы
      const toDate = offeringGroupParamsForm?.getFieldValue("toDate");

      offeringGroupParamsForm &&
        offeringGroupParamsForm.setFieldsValue({
          toDateRecord: toDate ? moment(toDate) : undefined,
        });

      setMainTreeData({
        id: Number(uniqueId()),
        offeringGroupMembers: [{}],
      });
    }
  }, [offeringGroup]);

  const [treeExpandedKeys, setTreeExpandedKeys] = useState<string[]>([]);

  const getTreeDataItem = (
    offeringGroup: Partial<IOfferingGroup>,
    parentKey?: string,
    offeringGroupMember?: Partial<IOfferingGroup>,
    offering?: Offer
  ): DataNodeOfferingGroup => {
    const id = offeringGroupMember?.id || offeringGroup?.id || Number(uniqueId());

    const key = [parentKey, id].filter(Boolean).join("-");

    if (offering) {
      return {
        offeringGroup,
        offeringGroupMember,
        offering,
        id,
        parentId: parentKey,
        title: offering?.name,
        key,
        className: "offering-group-table__row",
      };
    }
    return {
      ...offeringGroup,
      ...offeringGroupMember,
      offeringGroup,
      id,
      parentId: parentKey,
      title: offeringGroup?.name,
      key,
      children: offeringGroup?.offeringGroupMembers
        ? offeringGroup?.offeringGroupMembers?.flatMap((child, i) => {
            return getTreeDataItem(
              child.offeringGroup as Partial<IOfferingGroup>,
              key,
              child,
              child.offering
            );
          })
        : undefined,
      className: "offering-group-table__row",
    };
  };

  const treeData = useMemo((): DataNodeOfferingGroup[] => {
    if (mainTreeData) {
      return [
        getTreeDataItem({
          ...mainTreeData,
        }),
      ];
    }
    return [];
  }, [mainTreeData]);

  const { tableData } = useOfferingGroupTreeTableData({
    treeData: treeData?.[0],
  });

  const displayedTableData = useMemo(
    () =>
      tableData.filter((el) =>
        el?.parentId ? treeExpandedKeys.includes(String(el?.parentId)) : true
      ),
    [tableData, treeExpandedKeys]
  );

  const [parameterSearchType, setParameterSearchType] = useState<IParameterSearchType>(
    "offering_group"
  );

  const toggleParameterSearchType = () => {
    setParameterSearchType(parameterSearchType === "offering" ? "offering_group" : "offering");
  };

  const { activeModal, setActiveModal } = useActiveModal<"" | "offering" | "offering_group">("");

  const [savedSearchValues, setSavedSearchValues] = useState<IOfferListSearch<moment.Moment>>();

  const offerListProps = useOfferList({
    initialOfferListViewMode: "selectOffer",
    onReset: () => {
      setSavedSearchValues(undefined);
    },
    initialSearchValues: savedSearchValues || { onDate: moment(), page: 0, size: 10 },
  });

  const closeModal = () => {
    switch (activeModal) {
      case "offering":
        setSavedSearchValues(offerListProps.searchListValues);
        break;
      default:
        break;
    }
    setActiveModal("");
  };

  const submitOfferListModal = (offer: Offer) => {
    switch (activeModal) {
      case "offering":
        offeringGroupParamsForm &&
          offeringGroupParamsForm.setFieldsValue({
            objectName: offer.name,
          });

        setAddedOffering(offer);
        setSavedSearchValues(offerListProps.searchListValues);
        break;
      default:
        break;
    }
    closeModal();
  };

  const submitOfferingGroupsSearchModal = (offeringGroup: IOfferingGroup) => {
    switch (activeModal) {
      case "offering_group":
        offeringGroupParamsForm &&
          offeringGroupParamsForm.setFieldsValue({
            objectName: offeringGroup.name,
          });
        offeringGroup?.id &&
          dispatch(fetchOfferingGroupIdForTableTree({ offeringGroupId: offeringGroup.id })).then(
            (res) => {
              setAddedOfferingGroup(res.payload as IOfferingGroup);
            }
          );
        break;
      default:
        break;
    }
    closeModal();
  };

  const titleRender = (record: DataNodeOfferingGroupTree) => {
    const {
      title,
      key,
      offering,
      offeringGroup,
      offeringGroupMember,
      inversion,
      groupingTypeId,
    } = record;

    const isRoot = String(key).split("-").length === 1;
    //  Строка главная
    if (isRoot) {
      const groupingTypeName = getDictionaryItemById(offeringGroupsTypesDic, groupingTypeId)?.name;

      return (
        <div>
          {mode === "create" ? (
            <Row style={{ width: "100%" }} wrap={false}>
              <Form.Item dependencies={["groupingTypeId"]} noStyle>
                {({ getFieldValue }) => {
                  const groupingTypeIdForm = getFieldValue("groupingTypeId");
                  const groupingTypeIdFormName = getDictionaryItemById(
                    offeringGroupsTypesDic,
                    groupingTypeIdForm
                  )?.name;
                  return (
                    <div
                      className={`row-group-offering-root ${
                        groupingTypeIdFormName && groupingTypeIdFormName?.length > 3
                          ? "align-center-root"
                          : ""
                      }`}
                    >
                      {groupingTypeIdFormName
                        ? groupingTypeIdFormName?.replace(/^(.{3})(.*)$/, "$1 $2")
                        : null}
                    </div>
                  );
                }}
              </Form.Item>
              <Form.Item dependencies={["name"]} noStyle>
                {({ getFieldValue }) => {
                  const name = getFieldValue("name");
                  return (
                    <Form.Item className="form-item-required__hidden-error__mb0">
                      <h4>{name || "..."}</h4>
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Row>
          ) : (
            <Row style={{ width: "100%" }} wrap={false}>
              <div
                className={`row-group-offering-root ${
                  groupingTypeName && groupingTypeName?.length > 3 ? "align-center-root" : ""
                }`}
              >
                {groupingTypeId ? groupingTypeName?.replace(/^(.{3})(.*)$/, "$1 $2") : null}
              </div>
              <h4>{title}</h4>
            </Row>
          )}
        </div>
      );
    }
    if (!title) {
      // Строка создания элемента группы
      return (
        <Row style={{ width: "100%" }} wrap={false}>
          <div className="added-row">
            <Form.Item noStyle name="inversion">
              <Select
                allowClear
                style={{ width: 69, maxWidth: 69 }}
                options={[{ value: "true", label: "NOT" }]}
              />
            </Form.Item>
          </div>
          <Form.Item
            style={{ width: "100%" }}
            className="form-item-required__hidden-error__mb0 offering-group-table__parameter"
          >
            <Row wrap={false}>
              <Col>
                <Form.Item name="objectName" rules={[{ required: true, message: "" }]}>
                  <Input
                    allowClear
                    placeholder={
                      parameterSearchType === "offering"
                        ? "Выберите предложение"
                        : "Выберите группу предложений"
                    }
                    disabled
                    style={{ width: "100%", maxWidth: 220 }}
                  />
                </Form.Item>
              </Col>
              <Col span={3}>
                <Row wrap={false}>
                  {parameterSearchType === "offering" && (
                    <OfferingIcon
                      className="offering-group-table__offerIcon"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleParameterSearchType();
                      }}
                    />
                  )}
                  {parameterSearchType === "offering_group" && (
                    <GroupOfferingIcon
                      className="offering-group-table__offerIcon"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleParameterSearchType();
                      }}
                    />
                  )}
                  <Tooltip title="">
                    <LoupeIcon
                      className="offering-group-table__loupeIcon"
                      style={{ marginLeft: 10 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (mode === "create" || mode === "view") {
                          setActiveModal(parameterSearchType);
                        }
                      }}
                    />
                  </Tooltip>
                </Row>
              </Col>
            </Row>
          </Form.Item>
        </Row>
      );
    }
    if (offering) {
      // Строка предложения
      return (
        <Row style={{ width: "100%" }} wrap={false}>
          <div className="row-group-offering">{offeringGroupMember?.inversion ? "NOT" : null}</div>
          <div>
            {`${title || ""}`}
            <span className="cardinality-value">/</span>
            {`${offeringGroupMember?.offering?.extName || ""}`}
          </div>
        </Row>
      );
    }
    if (offeringGroup) {
      // Строка группы предложения

      const groupingTypeName = getDictionaryItemById(offeringGroupsTypesDic, groupingTypeId)?.name;

      return (
        <Row style={{ width: "100%" }} wrap={false}>
          <div
            className={`row-group-offering-not-absolute ${
              groupingTypeName && groupingTypeName?.length > 3 ? "align-center" : ""
            }`}
          >
            {groupingTypeId ? groupingTypeName?.replace(/^(.{3})(.*)$/, "$1 $2") : null}
          </div>
          <div className="row-group-offering">{inversion ? "NOT" : null}</div>
          <div>
            <Link to={`/offering-groups/${offeringGroup.id}`}>{title}</Link>
          </div>
        </Row>
      );
    }

    return <div>{title}</div>;
  };

  // Дефолтное открытие всех элементов дерева
  useEffect(() => {
    const allKeys: string[] = [];
    const addKey = ({ key, children }: DataNode) => {
      if (children && children.length > 0) {
        allKeys.push(String(key));
        children.forEach(addKey);
      }
    };
    treeData.forEach(addKey);
    setTreeExpandedKeys(allKeys);
  }, [treeData]);

  // По дефолту в keys остаются активными вложенные элементы свернутого узла, удаление таковых
  const onExpand = (keys: Key[], { expanded, node }) => {
    if (expanded) {
      setTreeExpandedKeys(keys.map(String));
    } else {
      setTreeExpandedKeys((prevKeys) => {
        const activeKeys = prevKeys.filter((key) => !String(key).startsWith(String(node.key)));
        return activeKeys;
      });
    }
  };

  // Отображение линий визуальных у дерева
  const showLine = useMemo(() => {
    return { showLeafIcon: false };
  }, []);

  const getOfferingGroupColumns = (): TableColumn<IOfferingGroupParamsTableRecord>[] => {
    return [
      {
        title: (
          <Row justify="center">
            <Tooltip title="умолч | мин | макс">Количество</Tooltip>
          </Row>
        ),
        key: "id",
        dataIndex: "id",
        width: columnsWidths.name,
        hidden: false,
        render: (_, record) => {
          // Кнопки в режиме создания
          if (mode === "create" || mode === "view") {
            const isRootSecondPos = record?.key?.split("-").length === 2;

            if (isRootSecondPos && !record?.offeringGroup && !record.offering) {
              return (
                <Row
                  justify="center"
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    alignItems: "center",
                  }}
                >
                  <Form.Item
                    name="defaultCardinality"
                    className="form-item-required__hidden-error__mb0"
                    rules={[
                      {
                        required: !!addedOffering,
                        message: "",
                      },
                    ]}
                  >
                    <Input style={{ width: 46, height: 32 }} disabled={!addedOffering} />
                  </Form.Item>
                  <span className="cardinality-value">|</span>
                  <Form.Item
                    name="minCardinality"
                    className="form-item-required__hidden-error__mb0"
                    rules={[
                      {
                        required: !!addedOffering,
                        message: "",
                      },
                    ]}
                  >
                    <Input style={{ width: 46, height: 32 }} disabled={!addedOffering} />
                  </Form.Item>
                  <span className="cardinality-value"> | </span>
                  <Form.Item
                    name="maxCardinality"
                    className="form-item-required__hidden-error__mb0"
                    rules={[
                      {
                        required: !!addedOffering,
                        message: "",
                      },
                    ]}
                  >
                    <Input style={{ width: 46, height: 32 }} disabled={!addedOffering} />
                  </Form.Item>
                </Row>
              );
            }
          }
          if (record.offering) {
            return (
              <Row justify="center" style={{ fontSize: 13, fontWeight: 700 }}>
                {`${record?.offeringGroupMember?.defaultCardinality || "-"}`}
                <span className="cardinality-value">|</span>
                {`${record?.offeringGroupMember?.minCardinality || "-"}`}
                <span className="cardinality-value"> | </span>
                {`${record?.offeringGroupMember?.maxCardinality || "-"}`}
              </Row>
            );
          }
          return null;
        },
      },

      {
        title: "Период",
        dataIndex: "id",
        key: "id",
        width: columnsWidths.fromDate,
        hidden: false,
        render: (_, record) => {
          // Кнопки в режиме создания
          if (mode === "create" || mode === "view") {
            const isRootSecondPos = record?.key?.split("-").length === 2;

            if (isRootSecondPos && !record?.offeringGroup && !record.offering) {
              return (
                <Row
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    alignItems: "center",
                  }}
                >
                  <Form.Item dependencies={["toDateRecord", "toDate", "fromDate"]} noStyle>
                    {({ getFieldValue }) => {
                      const toDateRecord = getFieldValue("toDateRecord");
                      const fromDate = getFieldValue("fromDate") || moment(offeringGroup?.fromDate);
                      const toDate = getFieldValue("toDate") || moment(offeringGroup?.toDate);
                      return (
                        <Tooltip
                          title={
                            !fromDate
                              ? 'Укажите: "Дату с" в параметрах группы'
                              : !toDate
                              ? 'Укажите: "Дату по" в параметрах группы'
                              : ""
                          }
                        >
                          <Form.Item
                            name="fromDateRecord"
                            rules={[
                              {
                                required: true,
                                message: "",
                              },
                              {
                                validator: (x, fromDateRecord) => {
                                  if (
                                    fromDateRecord &&
                                    fromDate &&
                                    moment(fromDateRecord).isBefore(moment(fromDate))
                                  ) {
                                    return Promise.reject();
                                  }
                                  return Promise.resolve();
                                },
                              },
                            ]}
                            className="form-item-required__hidden-error__mb0"
                          >
                            <DatePicker
                              format={displayFormat}
                              disabledDate={(current) => {
                                return (
                                  (toDateRecord && current.isAfter(toDateRecord, "day")) ||
                                  current.isAfter(toDate, "day") ||
                                  current.isBefore(fromDate) ||
                                  false
                                );
                              }}
                              style={{ width: "115px" }}
                              disabled={!fromDate || !toDate}
                            />
                          </Form.Item>
                        </Tooltip>
                      );
                    }}
                  </Form.Item>

                  <span className="cardinality-value">-</span>

                  <Form.Item dependencies={["fromDateRecord", "toDate", "fromDate"]} noStyle>
                    {({ getFieldValue }) => {
                      const fromDateRecord = getFieldValue("fromDateRecord");
                      const fromDate = getFieldValue("fromDate") || moment(offeringGroup?.fromDate);
                      const toDate = getFieldValue("toDate") || moment(offeringGroup?.toDate);

                      return (
                        <Tooltip
                          title={
                            !fromDate
                              ? 'Укажите: "Дату с" в параметрах группы'
                              : !toDate
                              ? 'Укажите: "Дату по" в параметрах группы'
                              : ""
                          }
                        >
                          <Form.Item
                            className="form-item-required__hidden-error__mb0"
                            name="toDateRecord"
                            rules={[
                              {
                                required: true,
                                message: "",
                              },
                              {
                                validator: (x, toDateRecord) => {
                                  if (
                                    toDateRecord &&
                                    toDate &&
                                    moment(toDateRecord).isAfter(moment(toDate))
                                  ) {
                                    return Promise.reject();
                                  }
                                  return Promise.resolve();
                                },
                              },
                            ]}
                          >
                            <DatePicker
                              format={displayFormat}
                              allowClear={false}
                              disabledDate={(current) => {
                                return (
                                  (fromDateRecord && current.isBefore(fromDateRecord, "day")) ||
                                  current.isBefore(fromDate, "day") ||
                                  current.isAfter(toDate, "day") ||
                                  false
                                );
                              }}
                              style={{ width: "115px" }}
                              disabled={!fromDate || !toDate}
                            />
                          </Form.Item>
                        </Tooltip>
                      );
                    }}
                  </Form.Item>
                </Row>
              );
            }

            const getDateFromString = () => {
              if (record.offeringGroupMember) {
                return `${
                  record?.offeringGroupMember?.fromDate &&
                  toDisplayFormat(record?.offeringGroupMember?.fromDate)
                }`;
              }
              return `${record?.fromDate && toDisplayFormat(record?.fromDate)}`;
            };

            if (isRootSecondPos && editedCloseDateRecord?.key === record?.key) {
              return (
                <Row
                  style={{
                    fontSize: 13,
                    alignItems: "center",
                  }}
                >
                  {getDateFromString()}

                  <span>&nbsp;-&nbsp;</span>

                  <Form.Item dependencies={["fromDateRecord", "toDate", "fromDate"]} noStyle>
                    {({ getFieldValue }) => {
                      const fromDateRecord = getFieldValue("fromDateRecord");
                      const fromDate = getFieldValue("fromDate") || moment(offeringGroup?.fromDate);
                      const toDate = getFieldValue("toDate") || moment(offeringGroup?.toDate);
                      return (
                        <Tooltip
                          title={
                            !fromDate
                              ? 'Укажите: "Дату с" в параметрах группы'
                              : !toDate
                              ? 'Укажите: "Дату по" в параметрах группы'
                              : ""
                          }
                        >
                          <Form.Item
                            className="form-item-required__hidden-error__mb0"
                            name="toDateRecord"
                            rules={[
                              {
                                required: true,
                                message: "",
                              },
                            ]}
                          >
                            <DatePicker
                              format={displayFormat}
                              allowClear={false}
                              disabledDate={(current) => {
                                return (
                                  (fromDateRecord && current.isBefore(fromDateRecord, "day")) ||
                                  current.isBefore(fromDate, "day") ||
                                  current.isAfter(toDate, "day") ||
                                  current.isAfter(
                                    moment(
                                      editedCloseDateRecord.offeringGroupMember
                                        ? editedCloseDateRecord.offeringGroupMember?.toDate
                                        : editedCloseDateRecord.toDate
                                    ),
                                    "day"
                                  )
                                );
                              }}
                              style={{ width: "115px" }}
                              disabled={!fromDate || !toDate}
                            />
                          </Form.Item>
                        </Tooltip>
                      );
                    }}
                  </Form.Item>
                </Row>
              );
            }
          }

          const isRoot = String(record.key).split("-").length === 1;

          if (isRoot) return null;

          if (record.offeringGroupMember) {
            return `${
              record?.offeringGroupMember?.fromDate &&
              toDisplayFormat(record?.offeringGroupMember?.fromDate)
            } - ${
              record?.offeringGroupMember?.toDate &&
              toDisplayFormat(record?.offeringGroupMember?.toDate)
            }`;
          }
          return `${record?.fromDate && toDisplayFormat(record?.fromDate)} - ${
            record?.toDate && toDisplayFormat(record?.toDate)
          }`;
        },
      },

      {
        dataIndex: "btns",
        key: "btns",
        width: "42px",
        style: { padding: "10px" },
        render: (v, record, idx) => {
          if (idx === 0) return null;

          // Проверка на то является ли главной группой у рутового предложения
          const isRootSecondPos = record?.key?.split("-").length === 2;

          // Очистка данных из формы и др при удалении строки в которой добавляем новый элемент
          const clearData = () => {
            setModeAddedRow(false);
            setAddedOfferingGroup(undefined);
            setAddedOffering(undefined);
            offerListProps.onResetSearch();
            offeringGroupParamsForm &&
              offeringGroupParamsForm.resetFields([
                "objectName",
                "fromDateRecord",
                "toDateRecord",
                "maxCardinality",
                "minCardinality",
                "defaultCardinality",
                "inversion",
              ]);
          };

          // Добавляем строку в иерархию (при создании вносим в стейт) - (в просмотре по id отправляем запрос и обновляем)
          const addRowOnTable = () => {
            offeringGroupParamsForm &&
              offeringGroupParamsForm
                .validateFields([
                  "toDateRecord",
                  "fromDateRecord",
                  "objectName",
                  "minCardinality",
                  "maxCardinality",
                  "defaultCardinality",
                ])
                .then(() => {
                  if (addedOfferingGroup) {
                    if (mode === "create") {
                      setMainTreeData((prev) => {
                        const formatOfferingGroupMembers: IOfferingGroupCreate["offeringGroupMembers"] = (
                          prev?.offeringGroupMembers || []
                        )
                          ?.slice(0, -1)
                          ?.concat({
                            id: 1,
                            fromDate: toHttpFormatStartOfDay(
                              offeringGroupParamsForm?.getFieldValue(["fromDateRecord"])
                            ),
                            toDate: toHttpFormatEndOfDay(
                              offeringGroupParamsForm?.getFieldValue(["toDateRecord"])
                            ),
                            memberTypeId: String(
                              offeringGroupsEntityObjectTypeDic?.find(
                                (el) => el.alias === "offering_group"
                              )?.id
                            ),
                            memberObjectId: addedOfferingGroup.id,
                            inversion:
                              offeringGroupParamsForm?.getFieldValue(["inversion"]) === "true",

                            offeringGroup: {
                              ...addedOfferingGroup,
                            },
                          });

                        return {
                          ...prev,
                          offeringGroupMembers: formatOfferingGroupMembers,
                        };
                      });
                      clearData();
                    }

                    if (mode === "view") {
                      const params = {
                        offeringGroupId: Number(offeringGroup?.id),
                        offeringGroupMembers: [
                          {
                            memberTypeId: String(
                              offeringGroupsEntityObjectTypeDic?.find(
                                (el) => el.alias === "offering_group"
                              )?.id
                            ),
                            memberObjectId: addedOfferingGroup.id,
                            fromDate: toHttpFormatStartOfDay(
                              offeringGroupParamsForm?.getFieldValue(["fromDateRecord"])
                            ),
                            toDate: toHttpFormatEndOfDay(
                              offeringGroupParamsForm?.getFieldValue(["toDateRecord"])
                            ),
                            inversion:
                              offeringGroupParamsForm?.getFieldValue(["inversion"]) === "true",
                          },
                        ],
                      };

                      dispatch(addedOfferingGroupMember(params)).then((res) => {
                        if (res?.payload) {
                          clearData();
                        }
                      });
                    }
                  }
                  if (addedOffering) {
                    if (mode === "create") {
                      setMainTreeData((prev) => {
                        return {
                          ...prev,
                          offeringGroupMembers: (prev?.offeringGroupMembers || [])
                            .slice(0, -1)
                            ?.concat({
                              ...offeringGroupParamsForm?.getFieldsValue([
                                "maxCardinality",
                                "minCardinality",
                                "defaultCardinality",
                              ]),
                              fromDate: toHttpFormatStartOfDay(
                                offeringGroupParamsForm?.getFieldValue(["fromDateRecord"])
                              ),
                              toDate: toHttpFormatEndOfDay(
                                offeringGroupParamsForm?.getFieldValue(["toDateRecord"])
                              ),
                              memberTypeId: offeringGroupsEntityObjectTypeDic?.find(
                                (el) => el.alias === "offering"
                              )?.id,
                              memberObjectId: addedOffering.id,
                              inversion:
                                offeringGroupParamsForm?.getFieldValue(["inversion"]) === "true",
                              offering: addedOffering,
                            }),
                        };
                      });
                      clearData();
                    }

                    if (mode === "view" && addedOffering?.id) {
                      const params = {
                        offeringGroupId: Number(offeringGroup?.id),
                        offeringGroupMembers: [
                          {
                            memberTypeId: String(
                              offeringGroupsEntityObjectTypeDic?.find(
                                (el) => el.alias === "offering"
                              )?.id
                            ),
                            memberObjectId: addedOffering.id,
                            fromDate: toHttpFormatStartOfDay(
                              offeringGroupParamsForm?.getFieldValue(["fromDateRecord"])
                            ),
                            toDate: toHttpFormatEndOfDay(
                              offeringGroupParamsForm?.getFieldValue(["toDateRecord"])
                            ),
                            inversion:
                              offeringGroupParamsForm?.getFieldValue(["inversion"]) === "true",

                            ...offeringGroupParamsForm?.getFieldsValue([
                              "maxCardinality",
                              "minCardinality",
                              "defaultCardinality",
                            ]),
                          },
                        ],
                      };

                      dispatch(addedOfferingGroupMember(params)).then((res) => {
                        console.log({ res });
                        if (res?.payload) {
                          clearData();
                        }
                      });
                    }
                  }
                });
          };

          // ------- Функции Удаления - Начало -------

          // Удаление строки в которой добавляем новый элемент
          const clearRowOnTable = () => {
            setMainTreeData((prev) => {
              return {
                ...prev,
                offeringGroupMembers: (prev?.offeringGroupMembers || []).slice(0, -1),
              };
            });
            clearData();
          };
          // Удаление не загруженной строки (в режиме создания или копирования)
          const handleDeleteOfferHierarchy = () => {
            setMainTreeData((prev) => {
              console.log({ record, prev });
              const newOfferingGroupMembers = (prev?.offeringGroupMembers || []).filter((member) =>
                record?.offeringGroup
                  ? member?.offeringGroup?.id !== record?.offeringGroup?.id
                  : member?.offering?.id !== record?.offering?.id
              );

              return {
                ...prev,
                offeringGroupMembers: newOfferingGroupMembers,
              };
            });
          };
          // Удаление загруженной строки (в режиме просмотра по id)
          const handleDeleteOfferOfferingGroupMember = () => {
            dispatch(
              deleteOfferingGroupMember({
                offeringGroupId: Number(offeringGroup?.id),
                memberId: Number(record?.id),
              })
            );
          };
          // ------- Функции Удаления - Конец -------

          // Изменение даты закрытия
          const handleUpdateCloseDateOfferingGroupMember = () => {
            const onDate = offeringGroupParamsForm?.getFieldValue("toDateRecord");
            if (onDate) {
              dispatch(
                updateOfferingGroupMemberCloseDate({
                  offeringGroupId: Number(offeringGroup?.id),
                  memberId: Number(editedCloseDateRecord?.id),
                  onDate: toHttpFormatEndOfDay(
                    offeringGroupParamsForm?.getFieldValue("toDateRecord")
                  ),
                })
              ).then(() => {
                setEditedCloseDateRecord(undefined);
              });
            }
          };
          // Модальное окно подтверждения удаления
          const onDelete = () => {
            Modal.confirm({
              content: "Вы уверены, что хотите удалить Предложение?",
              cancelText: "Отмена",
              onOk:
                mode === "create"
                  ? handleDeleteOfferHierarchy
                  : handleDeleteOfferOfferingGroupMember,
              cancelButtonProps: {
                style: {
                  width: "108px",
                },
              },
              okButtonProps: {
                style: {
                  width: "108px",
                },
              },
            });
          };

          // Кнопки в режиме создания
          if (mode === "create") {
            // Что отображать для первой вложенности, когда уже добавлено предложение или группа в иерархию
            if (isRootSecondPos && (record?.offeringGroup || record.offering)) {
              return (
                <Row justify="end" wrap={false}>
                  <DeleteIcon className="group-offer__deleteBtn" onClick={onDelete} />
                </Row>
              );
            }
            // Что отображать для первой вложенности, когда ещё не добавлено предложение или группа в иерархию
            if (isRootSecondPos && !record?.offeringGroup && !record.offering) {
              return (
                <Row justify="end" wrap={false}>
                  <Tooltip title="Подтвердить">
                    <SubmitIcon
                      onClick={addRowOnTable}
                      className={`group-offer__submitBtn ${
                        checkIsLoading(addedOfferingGroupMemberLoading) ? "pointer-event-none" : ""
                      }`}
                    />
                  </Tooltip>
                  <Tooltip title="Отменить">
                    <CancelIcon onClick={clearRowOnTable} className="group-offer__cancelBtn" />
                  </Tooltip>
                </Row>
              );
            }
            return;
          }

          // Кнопки в режиме просмотра группы
          if (mode === "view") {
            // Что отображать для первой вложенности, когда редактируется дата закрытия
            if (isRootSecondPos && editedCloseDateRecord?.key === record?.key) {
              return (
                <Row justify="end" wrap={false}>
                  <Tooltip title="Подтвердить">
                    <SubmitIcon
                      onClick={handleUpdateCloseDateOfferingGroupMember}
                      className={`group-offer__submitBtn ${
                        checkIsLoading(updateOfferingGroupMemberCloseDateLoading)
                          ? "pointer-event-none"
                          : ""
                      }`}
                    />
                  </Tooltip>
                  <Tooltip title="Отменить">
                    <CancelIcon
                      onClick={() => setEditedCloseDateRecord(undefined)}
                      className="group-offer__cancelBtn"
                    />
                  </Tooltip>
                </Row>
              );
            }
            // Что отображать для первой вложенности, когда уже добавлено предложение или группа в иерархию
            if (
              isRootSecondPos &&
              (record?.offeringGroup || record.offering) &&
              hasAccess(MENU_ADMIN_OFFERING_GROUP_WRITE)
            ) {
              return (
                <Row justify="end" wrap={false}>
                  <CloseIcon
                    onClick={() => {
                      setEditedCloseDateRecord(record);
                      offeringGroupParamsForm &&
                        offeringGroupParamsForm.setFieldsValue({
                          toDateRecord: record.offeringGroupMember
                            ? moment(record?.offeringGroupMember?.toDate)
                            : moment(record?.toDate),
                        });
                    }}
                    className="group-offer__closeBtn"
                  />
                  <DeleteIcon className="group-offer__deleteBtn" onClick={onDelete} />
                </Row>
              );
            }
            // Что отображать для первой вложенности, когда ещё не добавлено предложение или группа в иерархию
            if (isRootSecondPos && !record?.offeringGroup && !record.offering) {
              return (
                <Row justify="end" wrap={false}>
                  <Tooltip title="Подтвердить">
                    <SubmitIcon
                      onClick={addRowOnTable}
                      className={`group-offer__submitBtn ${
                        checkIsLoading(addedOfferingGroupMemberLoading) ? "pointer-event-none" : ""
                      }`}
                    />
                  </Tooltip>
                  <Tooltip title="Отменить">
                    <CancelIcon onClick={clearRowOnTable} className="group-offer__cancelBtn" />
                  </Tooltip>
                </Row>
              );
            }
            return;
          }

          return null;
        },
      },
    ];
  };

  const [columns, setColumns] = useState<TableColumn<IOfferingGroupParamsTableRecord>[]>(
    getOfferingGroupColumns()
  );

  useEffect(() => {
    setColumns(getOfferingGroupColumns());
  }, [
    modeAddedRow,
    addedOfferingGroup,
    addedOffering,
    editedCloseDateRecord,
    offeringGroup,
    addedOfferingGroupMemberLoading,
    updateOfferingGroupMemberCloseDateLoading,
  ]);

  const rowClassName = (record: IOfferingGroupParamsTableRecord) => {
    const isRootOffer = !record?.parentId;
    const baseClassName = "product-params__tableRow";
    if (isRootOffer) return baseClassName;
    // if (record.isCurrentVersion) {
    //   return `${baseClassName} row_child row_current-version`;
    // }
    // if (record.isPastVersion) {
    //   return `${baseClassName} row_child row_past-version`;
    // }
    return `${baseClassName}`;
  };

  return (
    <>
      <Collapse style={{ width: "100%" }} bordered={false} defaultActiveKey="1">
        <Collapse.Panel
          key="1"
          header={
            <Row
              style={{ width: "100%" }}
              justify="space-between"
              className="offer-create-container__pageTitle"
            >
              <Row style={{ width: "100%" }}>
                <h3>Элементы группы</h3>
                {hasAccess(MENU_ADMIN_OFFERING_GROUP_WRITE) && (
                  <Row justify="space-between">
                    <div />
                    <Button
                      style={{
                        color: "#00BB77",
                        marginLeft: "5px",
                        marginTop: "-2px",
                        border: "none",
                        background: "transparent",
                      }}
                      shape="circle"
                      className="button-icon"
                      icon={
                        <PlusCircleFilled
                          onClick={(e) => {
                            e.stopPropagation();
                            onClickPlus();
                          }}
                        />
                      }
                    />
                  </Row>
                )}
              </Row>
            </Row>
          }
        >
          <Row className="offering-group-table">
            <Col span={7}>
              <div className="offering-group-table__title">Наименование</div>
              <Tree
                showLine={showLine}
                treeData={treeData}
                showIcon={false}
                onExpand={onExpand}
                expandedKeys={treeExpandedKeys}
                titleRender={titleRender}
                selectable={false}
              />
            </Col>
            <Col span={17}>
              <Table
                size="small"
                columns={columns.filter(({ hidden }) => !hidden)}
                dataSource={displayedTableData}
                pagination={false}
                rowClassName={rowClassName}
                childrenColumnName="NONE"
              />
            </Col>
          </Row>
        </Collapse.Panel>
      </Collapse>

      <OfferListModal
        visible={parameterSearchType === "offering" && activeModal === "offering"}
        onOk={submitOfferListModal}
        onCancel={closeModal}
        offerListProps={{ ...offerListProps, filterName: "group-offer" }}
      />
      <OfferingGroupsSearchModal
        visible={parameterSearchType === "offering_group" && activeModal === "offering_group"}
        onCancel={closeModal}
        onSubmit={submitOfferingGroupsSearchModal}
      />
    </>
  );
};

export default OfferingGroupTreeTable;
